import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EnquiryThread } from "@/components/EnquiryThread";

/** Shown instead of Buy when the seller is on the display-only free tier. */
export function EnquiryBox({ productId, productTitle, user }) {
  const nav = useNavigate();
  const [msg, setMsg] = useState(`Hi, I'm interested in "${productTitle}". Is it still available?`);
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState(null);
  const signedIn = user && user !== false;

  useEffect(() => {
    if (!signedIn) return;
    api.get(`/enquiries/for-product/${productId}`).then(({ data }) => setThread(data.thread)).catch(() => {});
  }, [productId, signedIn]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/products/${productId}/enquire`, { message: msg });
      const { data } = await api.get(`/enquiries/for-product/${productId}`);
      setThread(data.thread);
      toast.success("Enquiry sent — replies arrive right here and by email");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="enquiry-box" className="w-full rounded-xl border border-[#27272A] bg-[#121212] p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 inline-flex items-center justify-center flex-shrink-0">
          <MessageSquare className="h-4 w-4 text-[#FF9F0A]" />
        </div>
        <div className="text-sm">
          <div className="font-bold text-white">Showcase listing — message the seller</div>
          <p className="text-neutral-400 mt-0.5">This seller is showing their products on VISMARKS. Buy now opens once they activate their seller plan and verify their ID.</p>
        </div>
      </div>
      {!signedIn ? (
        <Button data-testid="enquiry-signin-btn" onClick={() => nav(`/register?next=/product/${productId}`)}
          className="w-full h-11 rounded-full bg-[#FF9F0A] hover:bg-[#e68f09] text-black font-bold">
          Sign in free to enquire
        </Button>
      ) : thread ? (
        <div data-testid="enquiry-sent"><EnquiryThread thread={thread} side="buyer" onUpdate={setThread} /></div>
      ) : (
        <>
          <Textarea data-testid="enquiry-message" value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={1000} rows={3}
            className="bg-black/40 border-[#27272A] text-sm" />
          <Button data-testid="enquiry-send-btn" onClick={send} disabled={busy || msg.trim().length < 3}
            className="w-full h-11 rounded-full bg-[#FF9F0A] hover:bg-[#e68f09] text-black font-bold">
            <Send className="h-4 w-4 mr-2" /> {busy ? "Sending…" : "Send enquiry"}
          </Button>
        </>
      )}
      {signedIn && user.role === "seller" && (
        <p className="text-[11px] text-neutral-500">Selling your own products? <Link to="/billing" className="underline hover:text-white">See what unlocks Buy now</Link>.</p>
      )}
    </div>
  );
}
